import { createNamedApi, ensureArray, toHttpParams, snakeCase, createServiceApi, serviceTypes, combineEffectsWithProviders } from '@essent/common';
import * as i1 from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import { map } from 'rxjs/operators';
import * as i1$1 from '@ngrx/effects';
import { EffectsModule } from '@ngrx/effects';

/**
 * @ignore
 */
const API = new InjectionToken('api-financial');

/**
 * Maps [AccountBalanceResponse]{@link AccountBalanceResponse#source} to [AccountBalance]{@link AccountBalance#source}
 *
 * # <span style="color:red">**NOT EXPOSED BY PUBLIC_API!**</span>
 */
class AccountBalanceResponseMapper {
    map({ currentBalance }) {
        return {
            currentBalance: parseFloat(currentBalance)
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: AccountBalanceResponseMapper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: AccountBalanceResponseMapper }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: AccountBalanceResponseMapper, decorators: [{
            type: Injectable
        }] });

class AccountBalanceService {
    constructor(apiUrl, httpClient, accountBalanceResponseMapper) {
        this.apiUrl = apiUrl;
        this.httpClient = httpClient;
        this.accountBalanceResponseMapper = accountBalanceResponseMapper;
        this.apiVersion = '1';
        this.endpoint = ({ accountId }) => `${this.apiUrl || ''}/accounts/${accountId}/accountBalance/v${this.apiVersion}`;
    }
    /**
     * Performs a GET request to `/accounts/{accountId}/accountBalance/v${this.apiVersion}`.
     */
    call$(payload) {
        const headers = { Accept: 'application/json' };
        return this.httpClient
            .get(this.endpoint(payload), { headers })
            .pipe(map(response => this.accountBalanceResponseMapper.map(response)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: AccountBalanceService, deps: [{ token: API, optional: true }, { token: i1.HttpClient }, { token: AccountBalanceResponseMapper }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: AccountBalanceService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: AccountBalanceService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [API]
                }] }, { type: i1.HttpClient }, { type: AccountBalanceResponseMapper }] });

const getAccountBalanceApi = createNamedApi('[FinancialState] Get account balance', AccountBalanceService);

const { getAccountBalanceActions, getAccountBalance, getAccountBalanceSuccess, getAccountBalanceError, getAccountBalanceClear } = getAccountBalanceApi;

/**
 * Maps [FinancialLocksResponse]{@link FinancialLocksResponse#source} to [FinancialLocks]{@link FinancialLocks#source}
 *
 * # <span style="color:red">**NOT EXPOSED BY PUBLIC_API!**</span>
 */
class FinancialLocksResponseMapper {
    map({ locks }) {
        return {
            locks: ensureArray(locks.lock).map(lock => ({
                category: lock.category,
                reason: lock.reason,
                dateFrom: new Date(lock.date_from),
                dateTo: new Date(lock.date_to)
            })),
            ibanChangeable: locks.iban_changeable === 'true'
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialLocksResponseMapper, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialLocksResponseMapper }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialLocksResponseMapper, decorators: [{
            type: Injectable
        }] });

class FinancialLocksService {
    endpoint({ accountId, contractGroupId }) {
        return `${this.apiUrl || ''}/accounts/${accountId}/contractGroups/${contractGroupId}/locks/v${this.apiVersion}`;
    }
    constructor(apiUrl, httpClient, responseMapper) {
        this.apiUrl = apiUrl;
        this.httpClient = httpClient;
        this.responseMapper = responseMapper;
        this.apiVersion = '2';
    }
    /**
     * Performs a GET request to `/accounts/{accountId}/contractGroups/{contractGroupId}/locks/v{version}`.
     */
    call$(payload) {
        return this.httpClient
            .get(this.endpoint(payload))
            .pipe(map(response => this.responseMapper.map(response)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialLocksService, deps: [{ token: API, optional: true }, { token: i1.HttpClient }, { token: FinancialLocksResponseMapper }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialLocksService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialLocksService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [API]
                }] }, { type: i1.HttpClient }, { type: FinancialLocksResponseMapper }] });

const getFinancialLocksApi = createNamedApi('[FinancialState] Get financial locks', FinancialLocksService);

const { getFinancialLocksActions, getFinancialLocks, getFinancialLocksSuccess, getFinancialLocksError, getFinancialLocksClear } = getFinancialLocksApi;

class PatchPaymentDetailsService {
    constructor(apiUrl, httpClient) {
        this.apiUrl = apiUrl;
        this.httpClient = httpClient;
        this.apiVersion = '2';
        this.endpoint = `${this.apiUrl || ''}/contracting/contracts/paymentdetails/v${this.apiVersion}`;
    }
    /**
     * Performs a PATCH request to `/contracting/contracts/paymentdetails/v2`.
     */
    call$({ accountId, contractGroupId, contactlogId, paymentDetails }) {
        const params = toHttpParams(snakeCase({ accountId, contractGroupId, contactlogId }));
        const headers = { Accept: 'application/json' };
        const body = snakeCase(paymentDetails);
        return this.httpClient.patch(this.endpoint, body, { params, headers });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: PatchPaymentDetailsService, deps: [{ token: API, optional: true }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: PatchPaymentDetailsService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: PatchPaymentDetailsService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [API]
                }] }, { type: i1.HttpClient }] });

const patchPaymentDetailsApi = createNamedApi('[FinancialState] Patch payment details', PatchPaymentDetailsService);

const apiVersion = '2';
const getPaymentDetailsApi = createServiceApi('[FinancialState] Get payment details', 'get', serviceTypes(), {
    getUrl: `/contracting/contracts/paymentdetails/v${apiVersion}`,
    version: apiVersion,
    defaultHeaders: {
        Accept: 'application/json'
    },
    getParams: payload => snakeCase(payload)
});

const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(getAccountBalanceApi, getFinancialLocksApi, getPaymentDetailsApi, patchPaymentDetailsApi);
const MAPPERS = [AccountBalanceResponseMapper, FinancialLocksResponseMapper];
class FinancialCoreModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('FinancialCoreModule is imported more than once.');
        }
    }
    static forRoot(config) {
        return {
            ngModule: FinancialCoreModule,
            providers: [
                {
                    provide: API,
                    useValue: config ? config.api : undefined
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialCoreModule, deps: [{ token: FinancialCoreModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: FinancialCoreModule, imports: [i1$1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialCoreModule, providers: [...MAPPERS, ...PROVIDERS], imports: [EffectsModule.forFeature(EFFECTS)] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [EffectsModule.forFeature(EFFECTS)],
                    providers: [...MAPPERS, ...PROVIDERS]
                }]
        }], ctorParameters: () => [{ type: FinancialCoreModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });

const { patchPaymentDetailsActions, patchPaymentDetails, patchPaymentDetailsSuccess, patchPaymentDetailsError, patchPaymentDetailsClear } = patchPaymentDetailsApi;

const { getPaymentDetailsActions, getPaymentDetails, getPaymentDetailsSuccess, getPaymentDetailsError, getPaymentDetailsClear } = getPaymentDetailsApi;

const isPaymentTerm = (paymentChoice) => paymentChoice?.paymentTerm !== undefined;
const isDayOfPayment = (paymentChoice) => paymentChoice?.dayOfPayment !== undefined;
const isStreetAddress = (invoiceAddress) => invoiceAddress?.houseNumber !== undefined;
const isPoboxAddress = (invoiceAddress) => invoiceAddress?.pobox !== undefined;

/**
 * Generated bundle index. Do not edit.
 */

export { API, FinancialCoreModule, getAccountBalance, getAccountBalanceActions, getAccountBalanceClear, getAccountBalanceError, getAccountBalanceSuccess, getFinancialLocks, getFinancialLocksActions, getFinancialLocksClear, getFinancialLocksError, getFinancialLocksSuccess, getPaymentDetails, getPaymentDetailsActions, getPaymentDetailsClear, getPaymentDetailsError, getPaymentDetailsSuccess, isDayOfPayment, isPaymentTerm, isPoboxAddress, isStreetAddress, patchPaymentDetails, patchPaymentDetailsActions, patchPaymentDetailsClear, patchPaymentDetailsError, patchPaymentDetailsSuccess };
